import type {
  InterventionSectionContextType,
  InterventionSectionDatasetDeleteState,
  InterventionSectionProviderProps,
  InterventionSectionRedirectProps,
  InterventionSectionRedirectToProps,
} from "./intervention-section-context.type";
import type { InterventionDetail } from "@/entities/intervention";
import type { ObservationDetail } from "@/entities/observation";
import type { InterventionSectionItem } from "@/modules/intervention/intervention-section";

import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";

import { useGetInterventionTemplate } from "@/entities/intervention-template";
import {
  useGetObservationWithDatasets,
  useInvalidateObservation,
} from "@/entities/observation";
import { useDatasetsMutatorStatus } from "@/modules/datasets/datasets-mutator";
import { useGetInterventionMenu } from "@/modules/intervention/intervention-menu";
import {
  getMenuTreeItemKey,
  getMenuTreeItems,
} from "@/modules/intervention/intervention-menu-tree";
import {
  getInterventionSectionParams,
  getInterventionSectionTemplate,
  isDynamicSection,
} from "@/modules/intervention/intervention-section";

import {
  buildInterventionSectionSearchParams,
  getCurrentInterventionSection,
  getInterventionSectionSearchParams,
  getRedirectInterventionSection,
  getSectionSearchParams,
  getSectionSearchParamsWithTab,
  hasInterventionSectionParams,
} from "./intervention-section-context.helper";

export const useInterventionSectionProvider = ({
  interventionAsset,
  intervention,
}: InterventionSectionProviderProps): InterventionSectionContextType => {
  const { menu, ...menuQuery } = useGetInterventionMenu(intervention.id);

  const sections = menu ? [menu] : [];
  const rootLayoutIndex = menu?.layout_index;

  const [searchParams, setSearchParams] = useSearchParams();
  const urlParams = getInterventionSectionSearchParams(searchParams);
  const hasSectionParams = hasInterventionSectionParams(searchParams);

  // Retrieve current section based on url params. If no current section
  // found, always fallback to section template (root).
  const { section, parent: parentSection } = getCurrentInterventionSection({
    sections,
    urlParams,
  });
  const currentSection = hasSectionParams ? section : menu;

  const menuItems = getMenuTreeItems(sections);
  const menuSelectedKey = currentSection
    ? getMenuTreeItemKey(currentSection)
    : undefined;

  // Get `layoutIndex`, `assetId` & `observationId` values from url params. When
  // the current section is the section template (root), there is no params in
  // the url. Let's handle it by using `currentSection` as fallback value.
  const { layoutIndex, assetId, observationId } =
    getInterventionSectionSearchParams(searchParams, currentSection);

  const { onObservationCreate, onDatasetDelete } =
    useInterventionSectionRedirect({
      menuQuery,
      sections,
      currentSection,
      parentSection,
      layoutIndex,
      assetId,
    });

  const onSectionItemChange = (section?: InterventionSectionItem) => {
    // Update url search params with section item params
    const params = buildInterventionSectionSearchParams({
      section,
      rootLayoutIndex,
    });
    setSearchParams(params);
  };

  // Fetch current section's observation & datasets
  const { observation, datasets, ...observationQuery } =
    useGetObservationWithDatasets(observationId);

  // Fetch intervention's template & get current section's template
  const { template, ...templateQuery } = useGetInterventionTemplate(
    intervention.intervention_template_id
  );
  const sectionTemplate = getInterventionSectionTemplate(
    currentSection,
    template
  );

  useInvalidateObservationOnChange({ observation, intervention });

  return {
    // intervention data
    intervention,
    interventionAsset,
    isEditable: intervention.is_editable ?? false,

    // menu data
    sections,
    section: currentSection,
    parentSection,
    menuQuery,
    menuItems,
    menuSelectedKey,
    layoutIndex,
    assetId,
    observationId,

    // template data
    interventionTemplate: template,
    sectionTemplate,
    templateQuery,

    // observation & datasets data
    observation,
    observationQuery,
    sectionDatasets: datasets,

    // actions
    onObservationCreate,
    onDatasetDelete,
    onSectionItemChange,
  };
};

const useInterventionSectionRedirect = ({
  menuQuery,
  sections,
  currentSection,
  parentSection,
  layoutIndex,
  assetId,
}: InterventionSectionRedirectProps) => {
  const { dataUpdatedAt, fetchStatus } = menuQuery;
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const [newObservationId, setNewObservationId] = useState<number | undefined>(
    undefined
  );
  const [datasetDelete, setDatasetDelete] = useState<
    InterventionSectionDatasetDeleteState | undefined
  >(undefined);

  const onObservationCreate = (observationId: number) => {
    setNewObservationId(observationId);
  };

  const onDatasetDelete = () => {
    // backup current section params
    setDatasetDelete({ layoutIndex, assetId, parentSection });
  };

  const handleSectionChangedOnDatasetDelete = () => {
    if (!datasetDelete) return;

    // When section still exists, make sure datasets are up to date and stop here
    if (currentSection) {
      queryClient.invalidateQueries({ queryKey: ["observations", "get"] });
      return;
    }

    handleSectionChangedRedirect();
    setDatasetDelete(undefined);
  };

  const handleSectionChangedRedirect = () => {
    if (currentSection) return;

    const { layoutIndex, assetId, parentSection } = datasetDelete ?? {};

    // Try to find the same section without the observation id. We are in
    // this use case, when the user deleted the last dataset of a static
    // section. The section still exists but without the observation id
    // because the empty observation had been deleted with the dataset.
    if (layoutIndex) {
      const params = { layoutIndex, assetId };
      const newSection = getRedirectInterventionSection(sections, params);

      if (newSection) {
        redirectToSection({ params, keepTab: true, replace: true });
        return;
      }
    }

    // If the parent section is a dynamic section and it still exists in the
    // new fetch menu, then redirect to it!
    if (parentSection && isDynamicSection(parentSection)) {
      const params = getInterventionSectionParams(parentSection);
      const newSection = getRedirectInterventionSection(sections, params);

      if (newSection) {
        redirectToSection({ params, keepTab: true, replace: true });
        return;
      }
    }
  };

  const handleObservationCreateRedirect = () => {
    if (!newObservationId) return;

    const params = { observationId: newObservationId };
    redirectToSection({ params, keepTab: true });

    setNewObservationId(undefined);
  };

  const handleSectionNotFoundRedirect = () => {
    if (currentSection || newObservationId || datasetDelete) return;

    // If we arrive here, it means we do not have `currentSection` AND we do not have
    // infos to handle section redirection (based on local states). It means the url
    // params are just wrong for some reason. Let's redirect to root section.
    redirectToSection({ params: {}, keepTab: false, replace: true });
  };

  const redirectToSection = ({
    params,
    keepTab,
    replace = false,
  }: InterventionSectionRedirectToProps) => {
    const newParams = keepTab
      ? getSectionSearchParamsWithTab(searchParams, params)
      : getSectionSearchParams(params);
    setSearchParams(newParams, { replace });
  };

  useEffect(() => {
    if (fetchStatus !== "idle") return; // only continue if menu data is up to date

    handleSectionChangedOnDatasetDelete();
    handleObservationCreateRedirect();
    handleSectionNotFoundRedirect();
  }, [dataUpdatedAt, fetchStatus]);

  return {
    onObservationCreate,
    onDatasetDelete,
  };
};

export const useInvalidateObservationOnChange = ({
  observation,
  intervention,
}: {
  observation?: ObservationDetail;
  intervention: InterventionDetail;
}): void => {
  const observationId = observation?.id;
  const isObservationValidated = !!observation?.validation;

  const [shouldCheckInvalidation, setShouldCheckInvalidation] = useState(false);
  const { isFinished } = useDatasetsMutatorStatus();

  const { invalidate } = useInvalidateObservation(observation?.id ?? 0, {
    invalidateKeys: [["interventions", "menu", intervention.id]],
  });

  useEffect(() => {
    if (isFinished) setShouldCheckInvalidation(true);
  }, [isFinished]);

  useEffect(() => {
    setShouldCheckInvalidation(false); // reset
  }, [observationId]);

  useEffect(() => {
    if (shouldCheckInvalidation) {
      if (isObservationValidated) invalidate();
      setShouldCheckInvalidation(false);
    }
  }, [shouldCheckInvalidation, isObservationValidated]);
};
